import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { CssBaseline, ThemeProvider } from '@mui/material'
import {
  loadStoredThemeId,
  storeThemeId,
  themesById,
  type ThemeId,
} from '../theme'

type AppThemeContextValue = {
  themeId: ThemeId
  setThemeId: (id: ThemeId) => void
}

const AppThemeContext = createContext<AppThemeContextValue | null>(null)

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [themeId, setThemeIdState] = useState<ThemeId>(() => loadStoredThemeId())

  const setThemeId = useCallback((id: ThemeId) => {
    setThemeIdState(id)
    storeThemeId(id)
  }, [])

  const value = useMemo(() => ({ themeId, setThemeId }), [themeId, setThemeId])
  const theme = themesById[themeId]

  // A barra do sistema (Android e app instalado) com a cor da barra do topo do tema.
  useEffect(() => {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.palette.background.paper)
  }, [theme])

  return (
    <AppThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppThemeContext.Provider>
  )
}

export function useAppTheme(): AppThemeContextValue {
  const ctx = useContext(AppThemeContext)
  if (!ctx) {
    throw new Error('useAppTheme must be used within AppThemeProvider')
  }
  return ctx
}
